/**
 * Atlas — the active pack seam + scope routing (coordinator-owned).
 *
 * Two pools, joined here so the rest of the game reads one tidy API:
 *
 *   - **Landmarks** → `LANDMARKS_PACK` (`landmarksPack.ts`) — a large worldwide
 *     pool of real landmarks generated from Wikidata, each with the camera aimed
 *     at the subject and a factual one-liner. No difficulty. The Landmarks scope
 *     draws ONLY from here.
 *   - **Regional scopes** (world / continent / country / usState) → `REGIONAL_PACK`
 *     (`regionalPack.ts`) — town-anchored, difficulty-tiered. Addressed by pool
 *     tag AND a difficulty tier; the two together select the daily seed.
 *
 * `availableDifficulties(scope)` reports which tiers clear `MIN_POOL` so the
 * picker can gate the "How challenging?" step. `locationById` searches BOTH pools.
 */
import { LANDMARKS_PACK } from './landmarksPack';
import { REGIONAL_PACK } from './regionalPack';
import type { Difficulty, GeoLocation, Scope } from './schema';
import { DIFFICULTIES, poolTagForScope } from './schema';

/** The worldwide Landmarks corpus — camera-on-subject, factual blurb, no difficulty. */
export const LANDMARKS = LANDMARKS_PACK;
/** The town-anchored regional corpus — every location carries a `difficulty`. */
export const REGIONAL = REGIONAL_PACK;

export const LANDMARK_LOCATIONS: readonly GeoLocation[] = LANDMARKS.locations;
export const REGIONAL_LOCATIONS: readonly GeoLocation[] = REGIONAL.locations;

/** Every location across both pools — used by `locationById`. */
export const ALL_LOCATIONS: readonly GeoLocation[] = [
  ...LANDMARK_LOCATIONS,
  ...REGIONAL_LOCATIONS,
];

/**
 * Minimum pool size for a scope (at a given difficulty) to be OFFERED. A trip is
 * 3 distinct stops, so a scope needs a few more than that to feel varied day to
 * day. Thin pools (1–4 places) stay hidden rather than repeat the same spot.
 */
export const MIN_POOL = 5;

/** True for the Landmarks scope (the only one that reads the landmark pool). */
export function isLandmarksScope(scope: Scope): boolean {
  return scope.kind === 'landmarks';
}

/**
 * Locations for a scope, in stable pack order.
 *
 *   - Landmarks → the whole landmark pool (difficulty is irrelevant).
 *   - Regional scopes → the regional pool filtered by the scope's pool tag AND,
 *     when a `difficulty` is given, that tier. A regional scope WITHOUT a
 *     difficulty returns every tier for the tag (used for offerability checks).
 */
export function locationsForScope(scope: Scope, difficulty?: Difficulty): GeoLocation[] {
  const tag = poolTagForScope(scope);
  if (isLandmarksScope(scope)) {
    return LANDMARK_LOCATIONS.filter((l) => l.pools.includes(tag));
  }
  return REGIONAL_LOCATIONS.filter(
    (l) => l.pools.includes(tag) && (difficulty == null || l.difficulty === difficulty),
  );
}

/** True if the scope (at the difficulty, if any) has at least one location. */
export function scopeHasLocations(scope: Scope, difficulty?: Difficulty): boolean {
  return locationsForScope(scope, difficulty).length > 0;
}

/**
 * True if the scope is rich enough to OFFER (≥ {@link MIN_POOL}).
 *   - Landmarks → offerable when the landmark pool clears the bar.
 *   - Regional scopes → offerable when ANY difficulty tier clears the bar (the
 *     picker then only lists the tiers that individually clear it). Passing an
 *     explicit `difficulty` checks just that tier.
 */
export function scopeIsOfferable(scope: Scope, difficulty?: Difficulty): boolean {
  if (isLandmarksScope(scope)) {
    return locationsForScope(scope).length >= MIN_POOL;
  }
  if (difficulty != null) {
    return locationsForScope(scope, difficulty).length >= MIN_POOL;
  }
  return availableDifficulties(scope).length > 0;
}

/**
 * The difficulty tiers a regional scope can OFFER — those whose pool clears
 * {@link MIN_POOL}, in canonical order (classic → explorer → adventurer). Returns
 * `[]` for the Landmarks scope (it has no difficulty dimension).
 */
export function availableDifficulties(scope: Scope): Difficulty[] {
  if (isLandmarksScope(scope)) return [];
  return DIFFICULTIES.filter((d) => locationsForScope(scope, d).length >= MIN_POOL);
}

/** Look up a specific location by id across BOTH pools (used to resume a link). */
export function locationById(id: string): GeoLocation | null {
  return ALL_LOCATIONS.find((l) => l.id === id) ?? null;
}
